<template>
  <div class="videos" :style="{
      width: videoAreaWidth,
      height: videoAreaHeight,
    }">
    <div class="video" v-for="({vid, lr}) in videoSlots" :key="vid">
      <video :id="vid" class="video-js vjs-fluid" crossorigin playsinline>
      </video>
      <div class="glass-layer"
        @mouseenter="showVideoToolbox[vid] = true"
        @mouseleave="showVideoToolbox[vid] = false"
      >
        <div
          v-if="activeVjsLrMap[lr] && showVideoToolbox[vid]"
          class="video-toolbox"
          @click="$emit('toolbox-area-click', $event)"
        >
          <div class="tool-icon-wrap" @click="$emit('enter-fullscreen', lr)">
            <i class="tool fas fa-search-plus fullscreen"></i>
          </div>
          <i class="tool fa fa-spinner fa-spin" v-if="isDownloadingScreenshot"></i>
          <div class="tool-icon-wrap" @click="$emit('take-screenshot', lr, vid)" v-else>
            <i class="tool fas fa-camera dl-image"></i>
            <i class="fa fa-arrow-alt-circle-down mini-arrow"></i>
          </div>
          <i class="tool fa fa-spinner fa-spin" v-if="isDownloadingMovieFile"></i>
          <div class="tool-icon-wrap" @click="$emit('download-movie-file', lr)" v-else>
            <i class="tool fas fa-file-video"></i>
            <i class="fa fa-arrow-alt-circle-down mini-arrow"></i>
          </div>
        </div>
      </div>
      <div class="corner-triangle-label" v-show="isCurrentMovieReady[lr]"
        :style="{opacity: lr === 'left' ? 1.0 : 0.5}"></div>
      <div class="video-mask video-mask-black" v-show="!isCurrentMovieReady[lr]">
        <div class="error-text" v-if="movieSearch[lr].isResultEmpty">
          {{dtFormat(movieSearch[lr].params.dtFrom, 'yyyy/mm/dd')}}
          〜
          {{dtFormat(movieSearch[lr].params.dtTo, 'yyyy/mm/dd')}}
          の動画が見つかりませんでした
        </div>
      </div>
      <div class="video-mask video-mask-white"
        v-show="isCurrentMovieReady[lr] && !activeVjsLrMap[lr]"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import type { LeftRight, MovieSearchState } from 'src/models/moviePlayer';
import { dtFormat } from 'src/lib/dateHelper';

interface Props {
  videoAreaWidth: string;
  videoAreaHeight: string;
  vidLeft: string;
  vidRight: string;
  activeVjsLrMap: Record<string, boolean>;
  isCurrentMovieReady: { left: boolean; right: boolean };
  movieSearch: MovieSearchState;
  isDownloadingScreenshot: boolean;
  isDownloadingMovieFile: boolean;
}

const props = defineProps<Props>();

const videoSlots = computed<Array<{ vid: string; lr: LeftRight }>>(() => [
  { vid: props.vidLeft, lr: 'left' },
  { vid: props.vidRight, lr: 'right' },
]);

defineEmits<{
  (e: 'enter-fullscreen', lr: LeftRight): void;
  (e: 'take-screenshot', lr: LeftRight, vid: string): void;
  (e: 'download-movie-file', lr: LeftRight): void;
  (e: 'toolbox-area-click', event: Event): void;
}>();

// マウスホバー中のみツールボックスを表示する、この動画グリッド内に閉じた表示状態のため親stateには持たない
const showVideoToolbox = reactive<Record<string, boolean>>({});
</script>

<style lang="scss" scoped>
.videos {
  display: flex;
  justify-content: center;
  margin: 4px auto;
  .video {
    position: relative;
    margin: 0 4px;
    flex-grow: 1;
    overflow: hidden;
    z-index: 0; /* 何かつけとかないとhoverでtoolboxが表示されない */
  }
  .video-js {
    width: 100%;
    height: 100%;
    background-color: #ffffff;
    z-index: 25;
  }
  // video.js 7.20.3 の .vjs-fluid:not(.vjs-audio-only-mode) (0,3,0) は詳細度が高くて、
  // .video-js の height:100% を打ち消して height:0（アスペクト比表示）になる。
  // 同等の詳細度で height:100% を再指定し、従来どおりコンテナいっぱいに表示する。
  .video-js.vjs-fluid {
    height: 100%;
  }
  .video-mask {
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 100;
    top: 0;
    left: 0;
    &.video-mask-black { background-color: #000000; }
    &.video-mask-white { background-color: #ffffff; }
    .error-text {
      color: #ffffff;
      margin: 12px 14px;
    }
  }

  .glass-layer {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 50;
    background-color: transparent;
    .video-toolbox {
      display: flex;
      justify-content: flex-end;
      background-color: rgba(0, 0, 0, 0.5);
      color: #ffffff;
      font-size: 20px;
      height: 28px;
      padding: 0 2px 0 0;
      z-index: 60;
      .tool-icon-wrap {
        position: relative;
        width: 32px;
        cursor: pointer;
        text-align: center;
        .mini-arrow {
          position: absolute;
          right: 0px;
          bottom: 2px;
          font-size: 11px;
          border-radius: 8px;
          background-color: #777777;
        }
      }
      .tool {
        margin: 4px 6px;
        &.dl-video { margin-left: 4px; }
      }
    }
  }

  .corner-triangle-label {
    position: absolute;
    top: -15px;
    left: -15px;
    width: 30px;
    height: 30px;
    background-color: red;
    border: 1px solid #ffffff;
    transform: rotate(45deg);
    z-index: 70;
  }
}
</style>
